// Pair the messages of a chat session into exchanges for the conversation
// index (the bar rail on the left of the chat): one entry per user question,
// carrying its answer's stored `indexSummary` (or a fallback taken from the
// answer text), its time and a state (done / pending / error).

export function fallbackSummary(text, max = 160) {
  const t = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return t.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}

// Plain text of a message: `text` (display copy of block messages), a string
// content, or the text blocks of a block array.
export function messagePlainText(m) {
  if (!m) return "";
  if (typeof m.text === "string" && m.text) return m.text;
  const c = m.content;
  if (typeof c === "string") return c;
  if (Array.isArray(c)) {
    return c
      .filter(
        (p) =>
          p &&
          (p.type === "input_text" || p.type === "text") &&
          typeof p.text === "string"
      )
      .map((p) => p.text)
      .join("\n");
  }
  return "";
}

// Enough markdown removal for a one-line preview of an answer.
export function stripMarkdown(text) {
  return String(text || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/\|/g, " ")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1");
}

// messages: the session's visible messages in display order. Returns
// [{ key, userIndex, answerIndex, timestamp, question, summary,
//    summarySource: "ai"|"fallback"|"seed", answerLength, state }]
export function buildConversationIndex(
  messages,
  { pendingReply = false } = {}
) {
  const list = Array.isArray(messages) ? messages : [];
  const entries = [];

  list.forEach((m, i) => {
    if (!m || !m.role) return;

    if (m.role === "user") {
      const question = fallbackSummary(messagePlainText(m), 200);
      entries.push({
        key: m.uid || m.id || `idx-${i}`,
        userIndex: i,
        answerIndex: null,
        timestamp: m.timestamp || null,
        question: question || "Vraag",
        summary: "",
        summarySource: "fallback",
        answerLength: 0,
        state: "done",
        _questionUid: m.uid || m.id || null,
        _status: m.status || null,
      });
      return;
    }

    if (m.role !== "assistant") return;

    const answerText = messagePlainText(m);

    // A compression seed opens a session; it is its own entry.
    if (m.isCompressionSeed) {
      entries.push({
        key: m.uid || m.id || `seed-${i}`,
        userIndex: i,
        answerIndex: i,
        timestamp: m.timestamp || null,
        question: "Samenvatting vorige sessie",
        summary: fallbackSummary(stripMarkdown(answerText)),
        summarySource: "seed",
        answerLength: answerText.length,
        state: "done",
        _questionUid: null,
        _status: null,
      });
      return;
    }

    // Pair by questionUid (team mode writes it), else the OLDEST unanswered
    // question: queues answer first-in-first-out, and personal mode only ever
    // has one open question.
    let target = null;
    if (m.questionUid) {
      target = entries.find(
        (e) => e._questionUid && e._questionUid === m.questionUid
      );
    }
    if (!target) {
      target = entries.find(
        (e) => e.answerIndex === null && e.summarySource !== "seed"
      );
    }
    if (!target) return; // orphan answer (historical data)

    target.answerIndex = i;
    target.answerLength = answerText.length;
    if (typeof m.indexSummary === "string" && m.indexSummary.trim()) {
      target.summary = m.indexSummary.trim();
      target.summarySource = "ai";
    } else {
      target.summary = fallbackSummary(stripMarkdown(answerText));
      target.summarySource = "fallback";
    }
    if (!target.timestamp && m.timestamp) target.timestamp = m.timestamp;
  });

  entries.forEach((e, idx) => {
    if (e.answerIndex !== null) {
      e.state = "done";
    } else if (e._status === "error") {
      e.state = "error";
    } else if (
      e._status === "queued" ||
      e._status === "answering" ||
      (pendingReply && idx === entries.length - 1)
    ) {
      e.state = "pending";
    } else {
      e.state = "done";
    }
  });

  return entries.map((e) => {
    const out = { ...e };
    delete out._questionUid;
    delete out._status;
    return out;
  });
}

// ---------------------------------------------------------------------------
// Bar rail sizing (ChatIndexRail)

// How many bars fit on a track of `trackHeight` px when each bar needs `slot`
// px (its hit area plus the minimum gap).
export function railCapacity(trackHeight, slot = 10) {
  const h = Number(trackHeight) || 0;
  const s = Number(slot) > 0 ? Number(slot) : 10;
  return Math.max(1, Math.floor(h / s));
}

// Group consecutive index entries into at most `capacity` bars. With room for
// everyone each bar is one exchange; beyond that ceil(n / capacity) exchanges
// share a bar (the last one may be smaller). Nothing is dropped, so an old
// exchange stays reachable through its bucket's card.
export function bucketIndexEntries(entries, capacity) {
  const list = Array.isArray(entries) ? entries : [];
  if (!list.length) return [];
  const cap = Math.max(1, Math.floor(Number(capacity) || 1));
  const size = Math.max(1, Math.ceil(list.length / cap));
  const bars = [];
  for (let i = 0; i < list.length; i += size) {
    const group = list.slice(i, i + size);
    const first = group[0];
    const last = group[group.length - 1];
    let state = "done";
    if (group.some((e) => e.state === "pending")) state = "pending";
    else if (group.every((e) => e.state === "error")) state = "error";
    bars.push({
      key: group.length === 1 ? first.key : `${first.key}..${last.key}`,
      entries: group,
      first,
      question: first.question,
      summary: first.summary,
      time: first.time || null,
      timeEnd: group.length > 1 ? last.time || null : null,
      answerLength: Math.max(...group.map((e) => e.answerLength || 0)),
      state,
    });
  }
  return bars;
}
